import { type PropsWithChildren } from 'react';
import { Show } from '@elfsight/lib-utils';
import { WidgetI18nProvider } from '@elfsight/widget-i18n';
import { useCustomJS, useDefineAPISchema } from '@elfsight/widget-js-api';
import { PortalNestProvider } from '@elfsight/widget-modal';
import {
  CustomCSS,
  createCustomCSSPrefixClassName,
  EmbedRoot,
  ShadowEmbedRoot,
  useFontsLoader
} from '@elfsight/widget-ui';
import { useSettings } from '@wu/widget-core';
import DICTIONARY from '../language/dictionary.json';
import { meta } from './meta';
import type { JobBoardSettings } from './types';
import {
  FiltersWithThemeProvider,
  JobFilterProvider,
  SearchProvider
} from './widget/filters';
import { LayoutProvider } from './widget/layout';
import { PopupProvider } from './widget/popup';
import { Widget } from './widget/Widget';

function WidgetProviders({ children }: PropsWithChildren) {
  return (
    <PopupProvider>
      <LayoutProvider>
        <FiltersWithThemeProvider>
          <SearchProvider>
            <JobFilterProvider>{children}</JobFilterProvider>
          </SearchProvider>
        </FiltersWithThemeProvider>
      </LayoutProvider>
    </PopupProvider>
  );
}

export function JobBoard(_settings: JobBoardSettings) {
  const settings = useSettings();
  const {
    customCSS,
    customJS,
    font,
    editMode,
    widgetId,
    renderThumbnailMode,
    language,
    _shadowDOMDisabled
  } = settings;
  const customCSSPrefixClassName = createCustomCSSPrefixClassName(
    meta.appAlias,
    widgetId
  );

  useDefineAPISchema();
  useCustomJS(customJS, editMode || renderThumbnailMode);
  useFontsLoader([font]);

  return (
    <WidgetI18nProvider
      dictionary={DICTIONARY}
      language={language?.language ?? 'en'}
      override={language?.messages ?? {}}
    >
      <Show when={!_shadowDOMDisabled}>
        <ShadowEmbedRoot font={font} customCSS={customCSS}>
          <PortalNestProvider>
            <WidgetProviders>
              <Widget />
            </WidgetProviders>
          </PortalNestProvider>
        </ShadowEmbedRoot>
      </Show>
      <Show when={_shadowDOMDisabled === true}>
        <PortalNestProvider
          customCSSClassName={customCSSPrefixClassName}
          font={font}
        >
          <WidgetProviders>
            <CustomCSS
              customCSS={customCSS}
              prefixClassName={customCSSPrefixClassName}
            />
            <EmbedRoot
              className="RootLayout__RootComponent-sc" // legacy class
              customCSSClassName={customCSSPrefixClassName}
              font={font}
            >
              <Widget />
            </EmbedRoot>
          </WidgetProviders>
        </PortalNestProvider>
      </Show>
    </WidgetI18nProvider>
  );
}
